import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function ExportJobs() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["export-jobs", page],
    queryFn: () => get(`/exports?page=${page}&page_size=25`),
    refetchInterval: 4000,
  });

  const control = useMutation({
    mutationFn: ({ id, verb }) => api.post(`/exports/${id}/${verb}`),
    onSuccess: (_, { verb }) => {
      toast.success(`Export ${verb} requested`);
      queryClient.invalidateQueries({ queryKey: ["export-jobs"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const download = async (job) => {
    try {
      const { data: link } = await api.get(`/exports/${job.id}/download-link`);
      window.open(`${process.env.REACT_APP_BACKEND_URL}${link.url}`, "_blank");
      toast.success("Secure link valid for 15 minutes");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openRecords = async (job) => {
    try {
      const data = await get(`/exports/${job.id}/records?page=1&page_size=50`);
      setRecords({ job, ...data });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const tone = (status) => ({ completed: "completed", failed: "failed", cancelled: "failed",
    blocked: "failed", paused: "paused" }[status] || "running");

  return (
    <>
      <PageHeader
        testId="export-jobs-page"
        title="Export jobs"
        subtitle="Every export runs in the background and is recorded as an immutable batch"
      />

      <Section testId="export-jobs-section" title={`${data?.total ?? 0} exports`}>
        <DataTable
          testId="export-jobs-table"
          rowTestId="export-job-row"
          loading={isLoading}
          columns={[
            { key: "id", label: "Export", render: (r) => <span className="font-mono text-[11px]">{r.id.slice(0, 8)}</span> },
            { key: "scope", label: "Source", render: (r) => `${title(r.payload?.scope_type || "")}${r.payload?.scope_key ? ` · ${r.payload.scope_key.split(":")[1]?.slice(0, 8) || ""}` : ""}` },
            { key: "mode", label: "Mode", render: (r) => title(r.payload?.mode || "all") },
            { key: "format", label: "Format", render: (r) => (r.payload?.format || "csv").toUpperCase() },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={tone(r.status)} label={title(r.status)} testId={`export-status-${r.id}`} /> },
            { key: "progress", label: "Progress", render: (r) => (
              <div className="w-36 space-y-1">
                <Progress value={r.progress?.percent || 0} data-testid={`export-progress-${r.id}`} />
                <span className="text-[11px] text-slate-500">
                  {(r.batch?.rows ?? r.progress?.processed ?? 0).toLocaleString()} / {(r.progress?.total || 0).toLocaleString()} rows
                </span>
              </div>
            ) },
            { key: "columns", label: "Columns", render: (r) => (r.payload?.columns || []).length },
            { key: "requested_by_email", label: "Requested by" },
            { key: "created_at", label: "Requested", render: (r) => dt(r.created_at) },
            { key: "downloads", label: "Downloads", render: (r) => r.batch?.download_count ?? 0 },
            { key: "error", label: "Error", render: (r) => <span className="text-xs text-rose-600">{r.error || "—"}</span> },
            { key: "actions", label: "", render: (r) => (
              <div className="flex flex-wrap gap-1">
                {r.status === "completed" ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => download(r)} data-testid={`export-download-${r.id}`}>Download</Button>
                    <Button size="sm" variant="ghost" onClick={() => openRecords(r)} data-testid={`export-records-${r.id}`}>Included records</Button>
                  </>
                ) : null}
                {can("exports", "write") && ["processing", "queued"].includes(r.status) ? (
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => control.mutate({ id: r.id, verb: "cancel" })} data-testid={`export-cancel-${r.id}`}>Cancel</Button>
                ) : null}
                {can("exports", "write") && ["failed", "cancelled", "blocked"].includes(r.status) ? (
                  <Button size="sm" variant="ghost" onClick={() => control.mutate({ id: r.id, verb: "retry" })} data-testid={`export-retry-${r.id}`}>Retry</Button>
                ) : null}
              </div>
            ) },
          ]}
          rows={data?.items || []}
          empty={{ title: "No exports yet", description: "Export a list or smart segment to create your first batch." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <Dialog open={!!records} onOpenChange={(open) => !open && setRecords(null)}>
        <DialogContent className="max-w-2xl bg-white" data-testid="export-records-dialog">
          <DialogHeader>
            <DialogTitle>Included records — {(records?.total ?? 0).toLocaleString()} rows</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Filter snapshot: {JSON.stringify(records?.job?.payload?.filters || {})} · mode{" "}
            {records?.job?.payload?.mode}
          </p>
          <div className="max-h-80 overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr><th className="px-2 py-1.5 text-left">Phone</th><th className="px-2 py-1.5 text-left">Record</th><th className="px-2 py-1.5 text-left">Added</th></tr>
              </thead>
              <tbody>
                {(records?.items || []).map((item) => (
                  <tr key={item.record_id} className="border-t border-slate-100" data-testid={`record-${item.record_id}`}>
                    <td className="px-2 py-1.5 font-mono">{item.phone}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px]">{item.record_id.slice(0, 8)}</td>
                    <td className="px-2 py-1.5">{dt(item.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
